<template>
  <div class="gc-bom-bulk-update-process-config">
    <div class="config-card">
      <div class="config-card-header">
        <span class="config-card-title">Process Update Operation</span>
        <span class="config-card-title-secondary"
          >Based on process search criteria</span
        >
      </div>
      <div class="config-card-body">
        <span
          v-if="validated && !selectedOperation"
          class="param-error param-error-top"
        >
          Please select a process update operation.
        </span>

        <div class="operation-section">
          <div class="operation-section-header" v-if="selectedOperation">
            <DxButton
              text="Clear"
              type="default"
              hint="Clear selection"
              @click="selectedOperation = null"
              styling-mode="text"
              class="operation-clear-btn"
            />
          </div>

          <div :key="opListKey" class="operation-list">
            <div
              v-for="op in processOperations"
              :key="op.value"
              :class="[
                'operation-item',
                { 'op-active': selectedOperation === op.value },
              ]"
            >
              <!-- Radio header row -->
              <label class="op-header">
                <input
                  type="radio"
                  :value="op.value"
                  :checked="selectedOperation === op.value"
                  :disabled="isReadOnly"
                  class="op-radio"
                  @change="onOperationChange(op.value)"
                />
                <div class="op-text">
                  <span class="op-label">{{ op.label }}</span>
                  <span class="op-desc">{{ op.description }}</span>
                </div>
              </label>

              <!-- Inline param form — expands when this op is selected -->
              <div
                v-if="selectedOperation === op.value"
                class="op-params-inline"
              >
                <!-- UpdateRunRateAll -->
                <template v-if="op.value === 'UpdateRunRateAll'">
                  <div v-if="runRateRange" class="qty-range-context">
                    <span class="qty-range-label"
                      >CURRENT RUN RATE RANGE ACROSS MATCHED BOMS</span
                    >
                    <div class="qty-range-stats">
                      <div class="qty-stat-box">
                        <span class="qty-stat-key">Min</span>
                        <span class="qty-stat-val">{{
                          getFormattedQty(runRateRange.min)
                        }}</span>
                      </div>
                      <div class="qty-stat-box">
                        <span class="qty-stat-key">Max</span>
                        <span class="qty-stat-val">{{
                          getFormattedQty(runRateRange.max)
                        }}</span>
                      </div>
                      <div class="qty-stat-box">
                        <span class="qty-stat-key">Avg</span>
                        <span class="qty-stat-val">{{
                          getFormattedQty(runRateRange.avg)
                        }}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="runRateAllRows.length > 0"
                    class="replacement-header"
                  >
                    <span class="rt-col-code"
                      >Current Rate
                      <span class="qty-optional"
                        >(optional — exact match)</span
                      ></span
                    >
                    <span class="rt-col-rate"
                      >New Rate <span class="required-mark">*</span></span
                    >
                    <span class="rt-col-del"></span>
                  </div>
                  <div
                    v-for="(row, i) in runRateAllRows"
                    :key="i"
                    class="replacement-row"
                  >
                    <div class="rt-field">
                      <DxSelectBox
                        :key="'cur-rate-' + i + '-' + (row.currentRunRate || 'empty')"
                        :value="row.currentRunRate"
                        :data-source="allMatchedRunRates"
                        :show-clear-button="true"
                        placeholder="Any rate"
                        :disabled="isReadOnly"
                        @value-changed="row.currentRunRate = $event.value"
                      />
                    </div>
                    <div class="rt-field rt-field-rate">
                      <DxNumberBox
                        :value="row.newRunRate"
                        :min="0"
                        :show-spin-buttons="false"
                        :disabled="isReadOnly"
                        @value-changed="row.newRunRate = $event.value"
                        :format="'###,###,##0.0000'"
                      />
                      <span
                        v-if="
                          validated && rowErrors[i] && rowErrors[i].newRunRate
                        "
                        class="param-error"
                      >
                        {{ rowErrors[i].newRunRate }}
                      </span>
                    </div>
                    <DxButton
                      v-if="!isReadOnly"
                      icon="trash"
                      type="normal"
                      styling-mode="text"
                      class="rt-del-btn"
                      :disabled="runRateAllRows.length === 1"
                      @click="removeRunRateAllRow(i)"
                    />
                  </div>
                  <DxButton
                    v-if="!isReadOnly"
                    text="+ Add Row"
                    type="normal"
                    styling-mode="outlined"
                    class="add-row-btn"
                    @click="addRunRateAllRow"
                  />
                </template>

                <!-- UpdateRunRateNominated -->
                <template v-if="op.value === 'UpdateRunRateNominated'">
                  <div
                    v-if="runRateNominatedRows.length > 0"
                    class="replacement-header"
                  >
                    <span class="rt-col-code"
                      >Process Code <span class="required-mark">*</span></span
                    >
                    <span class="rt-col-rate"
                      >Current Rate
                      <span class="qty-optional"
                        >(optional — exact match)</span
                      ></span
                    >
                    <span class="rt-col-rate"
                      >New Rate <span class="required-mark">*</span></span
                    >
                    <span class="rt-col-del"></span>
                  </div>
                  <div
                    v-for="(row, i) in runRateNominatedRows"
                    :key="i"
                    class="replacement-row"
                  >
                    <div class="rt-field">
                      <DxSelectBox
                        :key="'proc-code-' + i + '-' + (row.processCode || 'empty')"
                        :value="row.processCode"
                        :data-source="
                          getDefaultNominatedProcessCodeDataSource(i)
                        "
                        display-expr="Code"
                        value-expr="Code"
                        :search-enabled="true"
                        :search-expr="['Code', 'Description']"
                        :drop-down-options="{ width: 300 }"
                        placeholder="Process code..."
                        :show-clear-button="true"
                        :disabled="isReadOnly"
                        @value-changed="
                          onNominatedProcessChange(row, $event.value)
                        "
                        item-template="process-item"
                      >
                        <template #process-item="{ data }">
                          <div class="custom-auto-complete">
                            <span class="auto-complete-primary">{{
                              data.Code
                            }}</span>
                            <span class="auto-complete-secondary">{{
                              data.Description
                            }}</span>
                          </div>
                        </template>
                      </DxSelectBox>
                      <span
                        v-if="
                          validated && rowErrors[i] && rowErrors[i].processCode
                        "
                        class="param-error"
                      >
                        {{ rowErrors[i].processCode }}
                      </span>
                    </div>
                    <div class="rt-field rt-field-rate">
                      <DxSelectBox
                        :key="'proc-cur-rate-' + i + '-' + (row.currentRunRate || 'empty')"
                        :value="row.currentRunRate"
                        :data-source="getNominatedRunRates(row.processCode)"
                        :show-clear-button="true"
                        placeholder="Any rate"
                        :disabled="isReadOnly"
                        @value-changed="row.currentRunRate = $event.value"
                      />
                    </div>
                    <div class="rt-field rt-field-rate">
                      <DxNumberBox
                        :value="row.newRunRate"
                        :min="0"
                        :show-spin-buttons="false"
                        :disabled="isReadOnly"
                        @value-changed="row.newRunRate = $event.value"
                        :format="'###,###,##0.0000'"
                      />
                      <span
                        v-if="
                          validated && rowErrors[i] && rowErrors[i].newRunRate
                        "
                        class="param-error"
                      >
                        {{ rowErrors[i].newRunRate }}
                      </span>
                    </div>
                    <DxButton
                      v-if="!isReadOnly"
                      icon="trash"
                      type="normal"
                      styling-mode="text"
                      class="rt-del-btn"
                      :disabled="runRateNominatedRows.length === 1"
                      @click="removeRunRateNominatedRow(i)"
                    />
                  </div>
                  <DxButton
                    v-if="!isReadOnly"
                    text="+ Add Row"
                    type="normal"
                    styling-mode="outlined"
                    class="add-row-btn"
                    @click="addRunRateNominatedRow"
                  />
                </template>

                <!-- ReplaceProcess / ReplaceProcessAndRunRate -->
                <template
                  v-if="
                    op.value === 'ReplaceProcess' ||
                    op.value === 'ReplaceProcessAndRunRate'
                  "
                >
                  <div
                    v-if="replacements.length > 0"
                    class="replacement-header"
                  >
                    <span class="rt-col-code">Old Code</span>
                    <span class="rt-col-code">New Code</span>
                    <span
                      v-if="op.value === 'ReplaceProcessAndRunRate'"
                      class="rt-col-rate"
                      >New Run Rate</span
                    >
                    <span class="rt-col-del"></span>
                  </div>
                  <div
                    v-for="(row, i) in replacements"
                    :key="i"
                    class="replacement-row"
                  >
                    <div class="rt-field">
                      <DxSelectBox
                        :value="row.OldCode"
                        :data-source="getOldCodeDataSource(i)"
                        display-expr="Code"
                        value-expr="Code"
                        :search-enabled="true"
                        :search-expr="['Code', 'Description']"
                        :drop-down-options="{ width: 300 }"
                        placeholder="Old code..."
                        :show-clear-button="true"
                        :disabled="isReadOnly"
                        @value-changed="onOldCodeChange(row, $event.value)"
                        item-template="process-item"
                        :key="'old-pp-' + i + '-' + (row.OldCode || 'empty')"
                      >
                        <template #process-item="{ data }">
                          <div class="custom-auto-complete">
                            <span class="auto-complete-primary">{{
                              data.Code
                            }}</span>
                            <span class="auto-complete-secondary">{{
                              data.Description
                            }}</span>
                          </div>
                        </template>
                      </DxSelectBox>
                      <span v-if="validated && !row.OldCode" class="param-error"
                        >Old code is required.</span
                      >
                      <span
                        v-if="row.OldCode && getProcessRef(row.OldCode)"
                        class="ref-hint"
                      >
                        Current rate:
                        {{
                          getFormattedQty(getProcessRef(row.OldCode).runRate)
                        }}
                      </span>
                    </div>
                    <div class="rt-field">
                      <DxSelectBox
                        :value="row.NewCode"
                        :data-source="getNewCodeDataSource(row.OldCode)"
                        display-expr="Code"
                        value-expr="Code"
                        :search-enabled="true"
                        :search-expr="['Code', 'Description']"
                        :drop-down-options="{ width: 300 }"
                        placeholder="New code..."
                        :show-clear-button="true"
                        :disabled="isReadOnly"
                        @value-changed="row.NewCode = $event.value"
                        item-template="process-item"
                        :key="'new-pp-' + i + '-' + (row.NewCode || 'empty')"
                      >
                        <template #process-item="{ data }">
                          <div class="custom-auto-complete">
                            <span class="auto-complete-primary">{{
                              data.Code
                            }}</span>
                            <span class="auto-complete-secondary">{{
                              data.Description
                            }}</span>
                          </div>
                        </template>
                      </DxSelectBox>
                      <span v-if="validated && !row.NewCode" class="param-error"
                        >New code is required.</span
                      >
                    </div>
                    <div
                      v-if="op.value === 'ReplaceProcessAndRunRate'"
                      class="rt-field rt-field-rate"
                    >
                      <DxNumberBox
                        :value="row.NewRunRate"
                        :min="0"
                        :show-spin-buttons="false"
                        :disabled="isReadOnly"
                        @value-changed="row.NewRunRate = $event.value"
                        :format="'###,###,##0.0000'"
                      />
                      <span
                        v-if="validated && !isValidRunRate(row.NewRunRate)"
                        class="param-error"
                        >Required (> 0).</span
                      >
                    </div>
                    <DxButton
                      v-if="!isReadOnly"
                      icon="trash"
                      type="normal"
                      styling-mode="text"
                      class="rt-del-btn"
                      @click="removeReplacementRow(i)"
                    />
                  </div>
                  <div
                    v-if="validated && replacements.length === 0"
                    class="param-error"
                  >
                    At least one replacement row is required.
                  </div>
                  <DxButton
                    v-if="!isReadOnly"
                    text="+ Add Row"
                    type="normal"
                    styling-mode="outlined"
                    class="add-row-btn"
                    @click="addReplacementRow"
                  />
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DxButton from "devextreme-vue/button";
import DxSelectBox from "devextreme-vue/select-box";
import { DxNumberBox } from "devextreme-vue/number-box";
import ApiService from "@/_services/api/api.service";
import { custom as dxCustomDialog } from "devextreme/ui/dialog";
import { formatNumber } from "devextreme/localization";

export default {
  name: "GcBomBulkUpdateProcessConfig",
  components: { DxButton, DxSelectBox, DxNumberBox },
  props: {
    value: { type: Object, default: null },
    matchedBomList: { type: Array, default: () => [] },
    isReadOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      processCodeDataSource: null,
      opListKey: 0,
      selectedOperation: null,
      previousOperation: null,
      runRateAllRows: [{ currentRunRate: null, newRunRate: null }],
      runRateNominatedRows: [
        { processCode: null, currentRunRate: null, newRunRate: null },
      ],
      replacements: [],
      validated: false,
      processOperations: [
        {
          value: "UpdateRunRateAll",
          label: "Update Run Rate — all matched processes",
          description:
            "Sets a new run rate on every process line matched by the search criteria across all included BOMs.",
        },
        {
          value: "UpdateRunRateNominated",
          label: "Update Run Rate — nominated process code",
          description: "Sets a new run rate for a specific process code only.",
        },
        {
          value: "ReplaceProcess",
          label: "Replace process",
          description:
            "Replaces one or more process codes. Existing run rates are preserved.",
        },
        {
          value: "ReplaceProcessAndRunRate",
          label: "Replace process and update run rate",
          description:
            "Replaces one or more process codes and sets a new run rate for each replacement line.",
        },
      ],
    };
  },
  computed: {
    hasExistingConfig() {
      return (
        this.runRateAllRows.some((r) => r.newRunRate != null) ||
        this.runRateNominatedRows.some(
          (r) => r.processCode != null || r.newRunRate != null,
        ) ||
        this.replacements.length > 0
      );
    },
    isValid() {
      return this.selectedOperation != null && this.buildErrors().length === 0;
    },
    runRateRange() {
      if (!this.matchedBomList || !this.matchedBomList.length) return null;
      let min = Infinity;
      let max = -Infinity;
      let sum = 0;
      let count = 0;
      for (const bom of this.matchedBomList) {
        const procs = bom.MatchedProcesses || bom.matchedProcesses;
        if (!procs) continue;
        for (const p of procs) {
          const rate = p.RunRate != null ? p.RunRate : p.runRate;
          if (rate != null) {
            min = Math.min(min, rate);
            max = Math.max(max, rate);
            sum += rate;
            count++;
          }
        }
      }
      if (!count) return null;
      return { min, max, avg: Math.round((sum / count) * 100) / 100 };
    },
    allMatchedRunRates() {
      if (!this.matchedBomList) return [];
      const seen = {};
      const rates = [];
      for (const bom of this.matchedBomList) {
        const procs = bom.MatchedProcesses || bom.matchedProcesses || [];
        for (const p of procs) {
          const rate = p.RunRate != null ? p.RunRate : p.runRate;
          if (rate != null && !seen[rate]) {
            seen[rate] = true;
            rates.push(rate);
          }
        }
      }
      return rates.sort((a, b) => a - b);
    },
    rowErrors() {
      const op = this.selectedOperation;
      const result = {};
      if (op === "UpdateRunRateAll") {
        this.runRateAllRows.forEach((row, i) => {
          result[i] = {};
          if (!this.isValidRunRate(row.newRunRate))
            result[i].newRunRate = "New Rate must be > 0.";
        });
      }
      if (op === "UpdateRunRateNominated") {
        this.runRateNominatedRows.forEach((row, i) => {
          result[i] = {};
          if (!row.processCode)
            result[i].processCode = "Process Code is required.";
          if (!this.isValidRunRate(row.newRunRate))
            result[i].newRunRate = "New Rate must be > 0.";
        });
      }
      return result;
    },
  },
  watch: {
    runRateAllRows: {
      deep: true,
      handler() {
        this.emitChange();
      },
    },
    runRateNominatedRows: {
      deep: true,
      handler() {
        this.emitChange();
      },
    },
    replacements: {
      deep: true,
      handler() {
        this.emitChange();
      },
    },
    selectedOperation() {
      this.emitChange();
    },
    value(newVal) {
      const incoming = JSON.stringify(newVal);
      const current = JSON.stringify(this.buildCurrentConfig());
      if (incoming !== current) {
        this.restoreFromValue(newVal);
      }
    },
  },
  created() {
    this._oldCodeDsCache = new Map();
    this._newCodeDsCache = new Map();
    this._nominatedProcessDsCache = new Map();
    if (this.value) {
      this.restoreFromValue(this.value);
    }
  },
  methods: {
    getFormattedQty(value) {
      return formatNumber(value, "##,###,###,###,##0.0000");
    },
    getProcessRef(code) {
      if (!code || !this.matchedBomList) return null;
      for (const bom of this.matchedBomList) {
        const procs = bom.MatchedProcesses || bom.matchedProcesses;
        if (!procs) continue;
        const found = procs.find(
          (p) => (p.Code || p.ProcessCode || p.ProcessCode) === code,
        );
        if (found != null) {
          return {
            runRate: found.RunRate != null ? found.RunRate : found.runRate,
          };
        }
      }
      return null;
    },
    isValidRunRate(val) {
      return val != null && val > 0;
    },
    getDefaultProcessCodeDataSource() {
      if (!this._defaultProcessCodeDs) {
        this._defaultProcessCodeDs = ApiService.createDataSource(
          "api/Process",
          {
            select: ["Code", "Description"],
            sort: "Code asc",
          },
        );
      }
      return this._defaultProcessCodeDs;
    },
    getOldCodeDataSource(rowIndex) {
      if (!this._oldCodeDsCache.has(rowIndex)) {
        this._oldCodeDsCache.set(
          rowIndex,
          ApiService.createDataSource("api/Process", {
            select: ["Code", "Description"],
            sort: "Code asc",
          }),
        );
      }
      return this._oldCodeDsCache.get(rowIndex);
    },
    getNewCodeDataSource(OldCode) {
      if (!OldCode) {
        return ApiService.createDataSource("api/Process", {
          select: ["Code", "Description"],
          sort: "Code asc",
        });
      }
      if (!this._newCodeDsCache.has(OldCode)) {
        this._newCodeDsCache.set(
          OldCode,
          ApiService.createDataSource("api/Process", {
            select: ["Code", "Description"],
            filter: [["Code", "<>", OldCode]],
            sort: "Code asc",
          }),
        );
      }
      return this._newCodeDsCache.get(OldCode);
    },
    getDefaultNominatedProcessCodeDataSource(rowIndex) {
      if (!this._nominatedProcessDsCache.has(rowIndex)) {
        this._nominatedProcessDsCache.set(
          rowIndex,
          ApiService.createDataSource("api/Process", {
            select: ["Code", "Description"],
            sort: "Code asc",
          }),
        );
      }
      return this._nominatedProcessDsCache.get(rowIndex);
    },
    getNominatedRunRates(processCode) {
      if (!processCode) return [];
      const seen = {};
      const rates = [];
      for (const bom of this.matchedBomList) {
        const procs = bom.MatchedProcesses || bom.matchedProcesses || [];
        for (const p of procs) {
          const code = p.Code || p.ProcessCode || p.code;
          if (code !== processCode) continue;
          const rate = p.RunRate != null ? p.RunRate : p.runRate;
          if (rate != null && !seen[rate]) {
            seen[rate] = true;
            rates.push(rate);
          }
        }
      }
      return rates.sort((a, b) => a - b);
    },
    addRunRateAllRow() {
      this.runRateAllRows.push({ currentRunRate: null, newRunRate: null });
    },
    removeRunRateAllRow(index) {
      if (this.runRateAllRows.length > 1) {
        this.runRateAllRows.splice(index, 1);
      }
    },
    addRunRateNominatedRow() {
      this.runRateNominatedRows.push({
        processCode: null,
        currentRunRate: null,
        newRunRate: null,
      });
    },
    removeRunRateNominatedRow(index) {
      if (this.runRateNominatedRows.length > 1) {
        this.runRateNominatedRows.splice(index, 1);
      }
    },
    onNominatedProcessChange(row, value) {
      row.processCode = value;
      row.currentRunRate = null;
    },
    onOldCodeChange(row, value) {
      if (row.OldCode !== value) {
        row.OldCode = value;
        row.NewCode = null;
      }
    },
    restoreFromValue(val) {
      if (!val) {
        this.selectedOperation = null;
        this.previousOperation = null;
        this.clearParams();
        return;
      }
      this.selectedOperation = val.Operation || null;
      this.previousOperation = val.Operation || null;
      if (val.Operation === "UpdateRunRateAll") {
        this.runRateAllRows =
          val.Params &&
          val.Params.RunRateUpdates &&
          val.Params.RunRateUpdates.length
            ? val.Params.RunRateUpdates.map((u) => ({
                currentRunRate:
                  u.CurrentRunRate !== undefined ? u.CurrentRunRate : null,
                newRunRate: u.NewRunRate !== undefined ? u.NewRunRate : null,
              }))
            : [{ currentRunRate: null, newRunRate: null }];
      } else if (val.Operation === "UpdateRunRateNominated") {
        this.runRateNominatedRows =
          val.Params &&
          val.Params.RunRateUpdates &&
          val.Params.RunRateUpdates.length
            ? val.Params.RunRateUpdates.map((u) => ({
                processCode: u.ProcessCode || null,
                currentRunRate:
                  u.CurrentRunRate !== undefined ? u.CurrentRunRate : null,
                newRunRate: u.NewRunRate !== undefined ? u.NewRunRate : null,
              }))
            : [{ processCode: null, currentRunRate: null, newRunRate: null }];
      } else if (
        val.Operation === "ReplaceProcess" ||
        val.Operation === "ReplaceProcessAndRunRate"
      ) {
        this.replacements =
          val.Params && val.Params.Replacements
            ? val.Params.Replacements.map((r) => Object.assign({}, r))
            : [];
      }
      this.validated = true;
    },
    async onOperationChange(newOp) {
      if (newOp === this.selectedOperation) return;
      if (this.hasExistingConfig) {
        const confirmed = await dxCustomDialog({
          title: "Change Operation",
          messageHtml:
            "Switching operation will clear your current configuration. Do you want to continue?",
          buttons: [
            { text: "Keep Current", onClick: () => false },
            {
              text: "Continue",
              type: "default",
              stylingMode: "contained",
              onClick: () => true,
            },
          ],
        }).show();
        if (!confirmed) {
          this.opListKey++;
          return;
        }
        this.clearParams();
      }
      this.previousOperation = this.selectedOperation;
      this.selectedOperation = newOp;
    },
    clearParams() {
      this.runRateAllRows = [{ currentRunRate: null, newRunRate: null }];
      this.runRateNominatedRows = [
        { processCode: null, currentRunRate: null, newRunRate: null },
      ];
      this.replacements = [];
    },
    addReplacementRow() {
      this.replacements.push({
        OldCode: null,
        NewCode: null,
        NewRunRate: null,
      });
    },
    removeReplacementRow(index) {
      this.replacements.splice(index, 1);
    },
    buildErrors() {
      const op = this.selectedOperation;
      const errors = [];
      if (!op) {
        errors.push("Please select a process update operation.");
        return errors;
      }
      if (op === "UpdateRunRateAll") {
        if (this.runRateAllRows.length === 0) {
          errors.push("At least one row is required.");
        } else {
          this.runRateAllRows.forEach((row, i) => {
            if (!this.isValidRunRate(row.newRunRate)) {
              errors.push(`Row ${i + 1}: New rate must be greater than 0.`);
            }
          });
        }
      }
      if (op === "UpdateRunRateNominated") {
        if (this.runRateNominatedRows.length === 0) {
          errors.push("At least one row is required.");
        } else {
          this.runRateNominatedRows.forEach((row, i) => {
            if (!row.processCode) {
              errors.push(`Row ${i + 1}: Process code is required.`);
            }
            if (!this.isValidRunRate(row.newRunRate)) {
              errors.push(`Row ${i + 1}: New rate must be greater than 0.`);
            }
          });
        }
      }
      if (op === "ReplaceProcess" || op === "ReplaceProcessAndRunRate") {
        if (this.replacements.length === 0) {
          errors.push("At least one replacement row is required.");
        }
        this.replacements.forEach((r, i) => {
          if (!r.OldCode) errors.push(`Row ${i + 1}: Old code is required.`);
          if (!r.NewCode) errors.push(`Row ${i + 1}: New code is required.`);
          if (
            op === "ReplaceProcessAndRunRate" &&
            !this.isValidRunRate(r.NewRunRate)
          ) {
            errors.push(`Row ${i + 1}: New rate must be greater than 0.`);
          }
        });
      }
      return errors;
    },
    buildCurrentConfig() {
      const op = this.selectedOperation;
      let params = {};
      if (op === "UpdateRunRateAll") {
        params.RunRateUpdates = this.runRateAllRows.map((row) => {
          const item = { NewRunRate: row.newRunRate };
          if (row.currentRunRate !== null && row.currentRunRate !== "") {
            item.CurrentRunRate = row.currentRunRate;
          }
          return item;
        });
      } else if (op === "UpdateRunRateNominated") {
        params.RunRateUpdates = this.runRateNominatedRows.map((row) => {
          const item = {
            ProcessCode: row.processCode,
            NewRunRate: row.newRunRate,
          };
          if (row.currentRunRate !== null && row.currentRunRate !== "") {
            item.CurrentRunRate = row.currentRunRate;
          }
          return item;
        });
      } else if (op === "ReplaceProcess") {
        params.Replacements = this.replacements.map((r) =>
          Object.assign({}, r),
        );
      } else if (op === "ReplaceProcessAndRunRate") {
        params.Replacements = this.replacements.map((r) =>
          Object.assign({}, r),
        );
      }
      return {
        Operation: op,
        Params: params,
      };
    },
    validate() {
      this.validated = true;
      return this.isValid;
    },
    emitChange() {
      const config = this.buildCurrentConfig();
      this.$emit("config-change", config);
      this.$emit("validation-change", { isValid: this.isValid });
    },
  },
};
</script>

<style lang="scss">
.gc-bom-bulk-update-process-config {
  .config-card {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
  }

  .config-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #c8b8f0;
    background: #ede8fb;
    border-radius: 6px 6px 0 0;

    .config-card-title-secondary {
      font-size: 12px;
      color: #6a4cb0;
    }
  }

  .config-card-title {
    font-size: 13px;
    font-weight: 600;
    color: #4a2c8a;
  }

  .config-card-body {
    padding: 16px;
  }

  .param-error-top {
    display: block;
    margin-bottom: 10px;
  }

  // Operation section
  .operation-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .operation-section-header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding-bottom: 8px;
    border-bottom: 1px solid #dee2e6;

    > .dx-button .dx-button-content {
      display: flex;
      align-items: center;
    }
  }

  .operation-section-title {
    font-weight: 500;
    font-size: 13px;
    color: #344054;
  }

  .operation-clear-btn {
    font-size: 12px;
    color: #6c757d;
    padding: 2px 6px;
    transition: color 0.15s;

    &:hover {
      color: #dc3545;
    }

    &:disabled {
      color: #dee2e6;
      cursor: not-allowed;
    }
  }

  // Operation radio list
  .operation-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .operation-item {
    display: flex;
    flex-direction: column;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    transition: border-color 0.15s, background 0.15s;
    overflow: hidden;

    &:hover {
      border-color: #03a9f4;
    }

    &.op-active {
      border-color: #03a9f4;
      background: #e8f6fe;
    }
  }

  .op-header {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    cursor: pointer;

    .op-radio {
      margin-top: 2px;
      flex-shrink: 0;
      accent-color: #03a9f4;
    }

    .op-text {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .op-label {
      font-size: 13px;
      font-weight: 500;
      color: #101828;
    }

    .op-desc {
      font-size: 12px;
      color: #6c757d;
    }
  }

  // Inline params — expands inside the active item
  .op-params-inline {
    border-top: 1px solid #b3e5fc;
    padding: 14px 12px 14px 34px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: #f0faff;
  }

  // Run rate range context
  .qty-range-context {
    margin-bottom: 4px;
  }

  .qty-range-label {
    display: block;
    font-size: 10px;
    font-weight: 700;
    color: #6c757d;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  .qty-range-stats {
    display: flex;
    gap: 8px;
  }

  .qty-stat-box {
    display: flex;
    flex-direction: row;
    gap: 2px;
    padding: 8px 14px;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    background: #f8f9fa;
    min-width: 80px;
    align-items: center;
  }

  .qty-stat-key {
    font-size: 10px;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  .qty-stat-val {
    font-size: 12px;
    font-weight: 600;
    color: #101828;
  }

  // Inline row (code + rate side-by-side)
  .param-row-inline {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    max-width: 480px;
  }

  .param-col {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;

    &.param-col-wide {
      flex: 2;
    }
    &.param-col-narrow {
      flex: 1;
      min-width: 120px;
      max-width: 180px;
    }
  }

  .param-label {
    font-size: 12px;
    font-weight: 500;
    color: #344054;
  }

  .required-mark {
    color: #dc3545;
    margin-left: 2px;
  }

  .qty-optional {
    font-size: 10px;
    color: #6c757d;
    margin-left: 4px;
    font-weight: 400;
  }

  .ref-hint {
    font-size: 11px;
    color: #6c757d;
    font-style: italic;
  }

  .param-error {
    display: block;
    font-size: 12px;
    color: #dc3545;
    margin-top: 2px;
  }

  // Replacement table
  .replacement-header {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 4px;

    .rt-col-code {
      flex: 1;
      font-size: 11px;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.4px;
    }

    .rt-col-rate {
      flex: 0 0 150px;
      font-size: 11px;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.4px;
    }

    .rt-col-del {
      width: 28px;
    }
  }

  .replacement-row {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin-bottom: 6px;

    .rt-del-btn {
      flex-shrink: 0;
      margin-top: 6px;
      color: #6c757d;

      &:hover {
        color: #dc3545;
      }
    }
  }

  .rt-field {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;

    .dx-selectbox,
    .dx-numberbox {
      width: 100%;
    }

    &.rt-field-rate {
      flex: 0 0 150px;
    }
  }

  .add-row-btn {
    margin-top: 4px;
    font-size: 13px;
  }
}
</style>
